import axios, { AxiosError } from "axios";
import { useCallback, useState } from "react";
import {
  FirebaseRefreshTokenResponse,
  FirebaseErrorResponse,
} from "src/entities/v3/FirebaseSignInRequest";

interface UseFirebaseRefreshTokenProps {
  onSuccess?: (response: FirebaseRefreshTokenResponse) => void;
  onError?: (error: AxiosError<FirebaseErrorResponse>) => void;
}

export const useFirebaseRefreshToken = ({
  onSuccess,
  onError,
}: UseFirebaseRefreshTokenProps = {}) => {
  const [isLoading, setIsLoading] = useState(false);

  const refreshToken = useCallback(
    async (currentRefreshToken: string) => {
      const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;

      if (!apiKey) {
        return null;
      }

      setIsLoading(true);

      try {
        // Firebase token refresh API requires application/x-www-form-urlencoded
        const params = new URLSearchParams();
        params.append("grant_type", "refresh_token");
        params.append("refresh_token", currentRefreshToken);

        const response = await axios.post<FirebaseRefreshTokenResponse>(
          `https://securetoken.googleapis.com/v1/token?key=${apiKey}`,
          params.toString(),
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        if (onSuccess) {
          onSuccess(response.data);
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.data;
      } catch (error) {
        const axiosError = error as AxiosError<FirebaseErrorResponse>;

        if (onError) {
          onError(axiosError);
        }

        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccess, onError]
  );

  return {
    refreshToken,
    isLoading,
  };
};
